import { LegacyRef, useEffect, useRef, useState } from 'react';
import mapboxgl, { LngLatBounds, Marker } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useLockersContext } from '../contexts/LockersContext';
import { LocalStorageFavouriteLocker, Locker, PointLocation } from '../data/LockerDataTypes';
import { Api } from '../utils/Api';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { useAccordionContext } from '../contexts/AccordionContext';
import { useSpecificLockerActionContext } from '../contexts/SpecificLockerActionContext';
import { useSystemTheme } from '../services/themeService';
import { Theme } from '../data/DesignTypes';
import { useFiltersContext } from '../contexts/FiltersContext';
import { POSIcon } from './Miscellaneous';
import ReactDOMServer from 'react-dom/server'
import { getOrderedFavouriteLockerTypes, setMarkersElementAsFavourite } from '../services/favouriteLockersService';
import ShowAllLockersComponent from './ShowAllLockersComponent';
import { isObjectNullOrEmpty } from '../services/lockerService';
import { ROUTE, STORAGE_BASE_URL } from '../Settings';
import { useLoadingContext } from '../contexts/LoadingContext';
import { MAPBOX_ACCESS_TOKEN } from '../Settings';

const MapComponent = (mapCenterProperties: any) => {
    const { t } = useTranslation();
    let selectedLockerRef = useRef(null as Locker | null);
    let searchFilterRef = useRef("")

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
    const {
        searchFilter,
        lockerResponse,
        geolocation,
        lockerPluginOptions,
        setUnfilteredLockerResponse
    } = useLockersContext();

    const { setAccordionExpanded } = useAccordionContext();

    const {
        setShowFilters,
        filterStrategies
    } = useFiltersContext();

    const {
        setSelectedLocker,
        selectedLocker,
        lockerListSelected,
        setLockerListSelected,
        favouriteLockers
    } = useSpecificLockerActionContext();

    const {
        firstLoadingInProgress,
        setFirstLoadingInProgress
    } = useLoadingContext();

    const imageUrl = `${STORAGE_BASE_URL}${ROUTE}`;
    const easyboxPin = `<img src=${imageUrl}/PinEasybox.png>`
    const easyboxPinHighlighted = `<img src=${imageUrl}/PinEasyboxHighlight.png>`
    const defaultZoom = 12;
    const widerZoom = 8;
    const maxZoom = 15;
    const minZoom = 5;
    const mapNavigationSpeed = 1.5;
    const mapNavigationCurve = 1.5;
    const mapContainer = useRef(null as any);
    const map = useRef(null as any);
    const [zoom] = useState(defaultZoom);
    const systemTheme = useSystemTheme();
    const [isMapMoving, setIsMapMoving] = useState(false);

    const getZoomLevel = () => {
        if (mapCenterProperties.zoomChangeRequired) {
            return widerZoom;
        }
        return zoom;
    }
    useEffect(() => {
        if (map.current) {
            return;
        } // initialize map only once

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: getMapboxTheme(),
            center: [mapCenterProperties.lng, mapCenterProperties.lat],
            zoom: getZoomLevel(),
            maxZoom: maxZoom,
            minZoom: minZoom
        });

        getLockersInArea();
        map.current.on('moveend', onMoveEnd);
        map.current.on('movestart', onMoveStart);
        map.current.on('mouseup', onMouseUp);
        map.current.dragRotate.disable();
        map.current.touchZoomRotate.disableRotation();

        addMapNavigationControl();
        addGeolocateControl();
    });

    useEffect(() => {
        popupAndHighlightSelectedLocker();
        selectedLockerRef.current = selectedLocker;
    }, [selectedLocker])

    useEffect(() => {
        clearAllExistingMarkersExceptLocationMarker();
        lockerResponse.forEach((l) => {
            addMarkerToMap(createMarkerElement(l), l);
        });
        addHiddenHighlightedMarker();
        drawFavouriteLockersOnMap();
        moveSpecialMarkersOnTop();
        firstTimeOpen();
        popupAndHighlightSelectedLocker();
        fitMarkersIntoBounds();
    }, [lockerResponse]);

    useEffect(() => {
        deselectPreviouslySelectedLocker();
        setSelectedLocker(lockerListSelected);
        setShowFilters(false);        
    }, [lockerListSelected]);

    useEffect(() => {
        searchFilterRef.current = searchFilter?.trim();
        if(searchFilterRef.current?.length <= 0){
            getLockersInArea();
        }    
    }, [searchFilter])

    useEffect(() => {
        if(lockerResponse.length <= 0){
            return;
        }

        drawFavouriteLockersOnMap();
    }, [favouriteLockers])

    useEffect(() => {
        if (!geolocation) { 
            return; 
        }

        let bounds = new mapboxgl.LngLatBounds();
        bounds.extend([geolocation.lng, geolocation.lat]);

        fitMapBounds(bounds, false);
        // Add markers to the map.
        new mapboxgl.Marker(getGeolocationMarker())
            .setLngLat(geolocation)
            .addTo(map.current);
        if (searchFilterRef.current?.length <= 0) {
            getLockersInArea();// TODO: check if need to refactor this behavior
        }

    }, [geolocation])

    const getMapboxTheme = () : string => {
        let theme = lockerPluginOptions?.theme && Object.keys(Theme).includes(lockerPluginOptions?.theme) ? lockerPluginOptions.theme : systemTheme;
        let style = 'mapbox://styles/mapbox/streets-v11';
        if (theme === Theme.dark) {
            style = `mapbox://styles/mapbox/${theme}-v10`
        }

        return style;
    }

    const getGeolocationMarker = (): HTMLDivElement => {
        const marker = document.createElement("div");
        marker.className = "custom-marker";
        const pin = document.createElement("div");
        pin.className = "custom-pin";      
        const pinEffect = document.createElement("div");
        pinEffect.className = "custom-pin-effect";

        marker.appendChild(pin);
        marker.appendChild(pinEffect);

        return marker;
    }

    const addMapNavigationControl = () : void =>{
        map.current.addControl(new mapboxgl.NavigationControl({
            showCompass: false,
            showZoom: true
        }));
    }

    const addGeolocateControl = () : void => {
        const geolocateControl = new mapboxgl.GeolocateControl({
            positionOptions: {
                enableHighAccuracy: false
            },
            showAccuracyCircle: false,
            fitBoundsOptions: {
                zoom: maxZoom,
                speed: mapNavigationSpeed,
                curve: mapNavigationCurve,
            }
        });

        map.current._myGeolocateControl = geolocateControl;
        map.current.addControl(geolocateControl);
    }

    const onMoveEnd = (eventData: any) => {
        setIsMapMoving(false)

        if (searchFilterRef.current?.length > 0) {
            return;
        }
        if (eventData?.originalEvent?.type === 'resize') {
            return;
        }
        getLockersInArea();
    };

    const onMoveStart = () =>{
        setIsMapMoving(true)
    }

    const onMouseUp = () => {
        if (map.current.isMoving()) {
            return;
        }
        setSelectedLocker({} as Locker);
        setLockerListSelected({} as Locker);
        setAccordionExpanded(false);
    };

    const fitMarkersIntoBounds = (): void => {
        if (lockerResponse.length < 1 || !searchFilterRef.current) {
            return;
        }
        
        const bounds = new mapboxgl.LngLatBounds();
        lockerResponse.forEach(function (locker) {
            bounds.extend([locker.lng, locker.lat]);
        });

        fitMapBounds(bounds, true);
    }

    const fitMapBounds = (bounds: LngLatBounds, fitOrigin: boolean): void =>{
        map.current.fitBounds(bounds, {
            padding: 30,
            speed: mapNavigationSpeed,
            curve: mapNavigationCurve,
        },
            { fitBoundsOrigin: fitOrigin }
        );
    }

    const getFavouriteLockerToSelect = (): Locker=>{
        let orderedFavouriteLockerTypes = getOrderedFavouriteLockerTypes();
        
        for(let favouriteLockerType of orderedFavouriteLockerTypes){
            let favouriteLocker = favouriteLockers.find(y => y.type === favouriteLockerType.type);
            let favouriteLockerToBeSelected = lockerResponse.filter(x => x.lockerId == favouriteLocker?.lockerId)[0];

            if(favouriteLockerToBeSelected){
                return favouriteLockerToBeSelected;
            }
        }

        return {} as Locker;
    }

    const firstTimeOpen = () => {
        const firstOpenWithResults = firstLoadingInProgress && lockerResponse.length > 0;
        if(firstLoadingInProgress){
            setFirstLoadingInProgress(false);
        }
        
        if (!firstOpenWithResults) {
            return;
        }
        
        setSelectedLocker(getFavouriteLockerToSelect());
    }

    const deselectPreviouslySelectedLocker = (): void => {
        if (!selectedLocker?.lockerId) {
            return;
        }

        const previousSelectedLocker = map.current._markers.filter((marker: any) => 
            marker.getElement().getAttribute('lockerId') == selectedLocker.lockerId)[0];
            
        if (previousSelectedLocker?.getPopup().isOpen()) {
            previousSelectedLocker.togglePopup(); //close popup
        }

        setSelectedLocker({} as Locker);        
    }

    const moveSpecialMarkersOnTop = (): void => {
        if (map.current._markers.length < 1) {
            return;
        }
        const specialMarkers = map.current._markers.filter((marker: any) => 
            marker.getElement().classList.contains('favourite'));

        specialMarkers.forEach((marker: any) => {
            marker.addTo(map.current);
        });

        const locationMarker = getMarkerByClass('custom-marker');
        if (locationMarker) {
            locationMarker.addTo(map.current)
        }
    }

    const addSelectedLockerIfDisappeared=(lockersInArea: Locker[]):void=>{
        if(isObjectNullOrEmpty(selectedLockerRef.current)){
            return;
        }

        const existingSelectedLocker=selectedLockerRef.current as Locker;
        const lngLatSelectedLocker = new mapboxgl.LngLat(existingSelectedLocker.lng, existingSelectedLocker.lat);
        const selectedLockerOnMapBounds = map.current.getBounds().contains(lngLatSelectedLocker);
        const selectedLockerInList = lockersInArea.find(locker => locker.lockerId === existingSelectedLocker.lockerId);
        
        if (!selectedLockerOnMapBounds || !selectedLockerInList) {
            return;
        }
        
        lockersInArea.push(existingSelectedLocker);
    }

    const getLockersInArea = (): void => {
        const mapBounds = map.current.getBounds();
        const ne = {lng: mapBounds._ne.lng, lat: mapBounds._ne.lat} as PointLocation;
        const sw = {lng: mapBounds._sw.lng, lat: mapBounds._sw.lat} as PointLocation;
        trackPromise(Api.Get().GetLockersInArea(ne, sw, lockerPluginOptions.countryCode).then(f => {            
            addSelectedLockerIfDisappeared(f);
            setUnfilteredLockerResponse(f);
        }));
    }

    const createMarkerElement = (l: Locker): HTMLDivElement => {
        const el = document.createElement('div');
        el.className = 'marker';
        el.style.backgroundSize = '100%';
        el.setAttribute("lockerId", l.lockerId.toString())
        el.innerHTML = easyboxPin;
        el.addEventListener('click', () => {
            setSelectedLocker(l);
            setShowFilters(false);
        });
        return el;
    };

    const addMarkerToMap = (el: HTMLDivElement, l: Locker): void => {
        let marker = new mapboxgl.Marker(el)
            .setLngLat([l.lng, l.lat])
            .setPopup((new mapboxgl.Popup({ offset: 25, focusAfterOpen: false }))
                .setHTML(getMarkerHtmlPopup(l)),
            );

        marker.addTo(map.current);
    };

    const addHiddenHighlightedMarker = (): void => {
        const el = document.createElement('div');
        el.className = 'marker highlight';
        el.style.backgroundSize = '100%';
        el.style.display = 'none';
        el.innerHTML = easyboxPinHighlighted;
        let marker = new mapboxgl.Marker(el)
            .setLngLat([0, 0]);
        marker.addTo(map.current);
    }

    const highlightMarker = (): void => {
        const markerAlreadyHighlighted = getMarkerByClass('highlight');

        if (!markerAlreadyHighlighted) {
            return;
        }

        if (markerAlreadyHighlighted.getElement().style.display == 'flex') {
            markerAlreadyHighlighted.getElement().style.display = 'none';
            return;
        }

        if (selectedLocker?.lat && selectedLocker?.lng) {
            markerAlreadyHighlighted.getElement().style.display = 'flex'
            markerAlreadyHighlighted.setLngLat([selectedLocker.lng, selectedLocker.lat])
        }
    }

    const popupAndHighlightSelectedLocker = (): void => {
        const selectedLockerFromMap = map.current._markers.filter((marker: any) =>
            marker.getElement().getAttribute('lockerId') == selectedLocker.lockerId)[0];

        if(isObjectNullOrEmpty(selectedLockerFromMap)){
            return;
        }

        highlightMarker();

        const popup = selectedLockerFromMap.getPopup();
        if (popup && popup.isOpen() === false) {
            selectedLockerFromMap.togglePopup();
        }
    }

    const drawFavouriteLockersOnMap = (): void => {
        removeFavouriteLockersMarkers();

        favouriteLockers.forEach(favouriteLocker => {
            let newFavouriteLockerFromMap = getFavoriteLockerMarker(favouriteLocker);
            const orderedFavouriteLockerTypes = getOrderedFavouriteLockerTypes();   
            const icon = orderedFavouriteLockerTypes.filter(x => x.type == favouriteLocker.type)[0].icon;        
            setMarkersElementAsFavourite(newFavouriteLockerFromMap, icon);
        });
    }
 
    const removeFavouriteLockersMarkers = () : void => {
        const markerAlreadyFav = map.current._markers.filter((marker: any) => marker.getElement().classList.contains('favourite'));

        for (let i = 0; i < markerAlreadyFav.length; i++) {
                markerAlreadyFav[i].getElement().classList.remove('favourite')
                markerAlreadyFav[i].getElement().innerHTML = easyboxPin;
        }
    }

    const getFavoriteLockerMarker = (favouriteLocker: LocalStorageFavouriteLocker): Marker => {
        return map.current._markers.filter((marker: any) =>
            marker.getElement().getAttribute('lockerId') == favouriteLocker.lockerId)[0];
    }

    const getMarkerByClass = (className: string): any => {
        return map.current._markers.filter((marker: any) => marker.getElement().classList.contains(className))[0]
    }

    const clearAllExistingMarkersExceptLocationMarker = (): void => {
        const location = getMarkerByClass('custom-marker');

        while (map.current._markers.length > 0) {
            map.current._markers[map.current._markers.length - 1].remove();
        }

        if (location) {
            location.addTo(map.current);
        }
    }

    const getMarkerHtmlPopup = (locker: Locker): string => {
        return `<h5>${locker.name}</h5>
        <div>${locker.address}, ${locker.city}, ${locker.county}</div>
        <div className='mt-1 d-flex justify-content-center align-items-center'>
        ${ReactDOMServer.renderToString(<POSIcon />)}
        <strong className='a-bit-of-margin-left'>${t('posAtLocation')} ${locker.supportedPayment == 1 ? t('yes') : t('no')}</strong>
        </div> 
        <a href="https://www.google.ro/maps/place/${locker.lat},${locker.lng}" target="_blank">${t("openOnGoogleMaps")}</a>
        `;
    };

    const showAllLockersButton = (): boolean => {   
        const searchOrFilterApplied = searchFilter || filterStrategies.find(x => x.implementation.IsApplicable());
        if(!map.current || isMapMoving || !searchOrFilterApplied)
        {
            return false;
        }
        return true;
    }

    return (
        <div >
            <div style={{ height: '100%',    width: '100%', minHeight: '300px', textAlign: 'center', alignContent: 'center' }} ref={mapContainer as LegacyRef<HTMLDivElement>} className="map-container" >
                {
                   showAllLockersButton() && <ShowAllLockersComponent mapBounds={map.current.getBounds()}/>
                }
            </div>
        </div>
    );
};

export default MapComponent;

